<template>
  <div
    v-if="!isItemFreeGift"
    class="line-item"
    :class="[
      $blockClass(blockClass, 'line-item'),
      {
        [$class('loading')]: !item,
        [$class('updating')]: state === 'updating'
      },
    ]"
    role="rowgroup"
  >
    <div
      class="line-item__row"
      :class="$blockClass(blockClass, 'line-item__row')"
    >
      <a
        class="line-item__row--image"
        :class="$blockClass(blockClass, 'line-item__row--image')"
        :href="item.url"
      >
        <responsive-image
          :class="$blockClass(blockClass, 'line-item-image')"
          block-class="line-item"
          :image="item.featured_image || item.image"
          min-max="600-1240"
          ratio="4:5"
          sizes="1200px"
          :style="{ animationDelay: animationDelay }"
        />
      </a>
      <div class="line-item__body">
        <p
          v-if="productCollection"
          class="line-item__collection text-utility-label-xs"
          :class="$blockClass(blockClass, 'line-item-collection')"
          v-text="productCollection"
        />
        <a
          class="line-item__title  text-utility-capitalized-s"
          :class="$blockClass(blockClass, 'line-item-title')"
          :href="item.url"
          v-text="item.product_title || item.title"
        />
        <p
          v-if="item?.variant_options?.length > 0 && !isGiftWrapProduct && !isFreeGift"
          class="line-item__size  text-utility-capitalized-s"
          :class="$blockClass(blockClass, 'line-item-size')"
          v-text="`${$string('cart.line_item.size')}: ${item.variant_options[0]}`"
        />
        <div
          v-if="isMadeToOrderProduct && !isDrawer && !isMobile && !isFreeGift"
          class="line-item__type"
        >
          <p
            class="text-utility-capitalized-s"
            v-html="$string('cart.line_item.made_to_order_html')"
          />
        </div>
        <div
          v-if="isDrawer || isMobile || isFreeGift"
          class="line-item__mobile"
        >
          <product-prices
            block-class="line-item"
            class="text-utility-capitalized-s"
            :class="$blockClass(blockClass, 'line-item-prices')"
            :is-free-gift="isFreeGift"
            :compare-at-price="item?.compare_at_line_price || false"
            :price="item?.final_line_price || (item?.variants || [])[0]?.price"
          />
          <p
            v-if="isFreeGift"
            class="text-utility-capitalized-s"
            v-text="$string('cart.free_gift.gift_target',
                            { replace: { value: $formatMoney(freeGiftTarget) } })"
          />
          <div
            v-if="isMadeToOrderProduct && !isFreeGift"
            class="line-item__type"
          >
            <p
              class="text-utility-capitalized-s"
              v-html="$string('cart.line_item.made_to_order_html')"
            />
          </div>
          <quantity-selector
            v-if="!isGiftWrapProduct && !isFreeGift"
            :class="$blockClass(blockClass, 'line-item-quantity')"
            block-class="line-item"
            :item="item"
            :quantity="quantity"
            :state="state"
            @update-quantity="handleQuantityUpdate"
          />
        </div>
      </div>
    </div>
    <div
      v-if="!isDrawer"
      class="line-item__row desktop"
    >
      <quantity-selector
        v-if="!isGiftWrapProduct && !isFreeGift"
        :class="$blockClass(blockClass, 'line-item-quantity')"
        block-class="line-item"
        :item="item"
        :quantity="quantity"
        :state="state"
        @update-quantity="handleQuantityUpdate"
      />
    </div>
    <div
      v-if="!isDrawer"
      class="line-item__row desktop"
    >
      <product-prices
        v-if="!isFreeGift"
        block-class="line-item"
        class="text-utility-capitalized-s"
        :class="$blockClass(blockClass, 'line-item-prices')"
        :compare-at-price="item?.compare_at_line_price || false"
        :price="item?.final_line_price"
      />
    </div>
    <div class="line-item__row remove">
      <button
        v-if="!isFreeGift"
        class="line-item__remove text-body-s-link"
        :class="[
          $blockClass(blockClass, 'line-item-remove'),
          { [$class('disabled')]: state === 'updating' },
        ]"
        type="button"
        :aria-label="$string('cart.line_item.remove')"
        @click="handleRemove"
      >
        {{ $string('cart.line_item.remove') }}
      </button>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Line item (line-item)
 * -----------------------------------------------------------------------------
 * Displays line items in cart, cart drawer, and order.
 *
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Number} [index] - Line item's forloop index.
 * @param {Boolean|Object} item - Line item in cart response or order.
 * @param {Boolean} isDrawer - Is it for cart drawer.
 *
 */
import { mapActions, mapState } from 'vuex'

import ProductPrices from '~global/product-prices/product-prices'
import QuantitySelector from '~async/quantity-selector/quantity-selector'
import ResponsiveImage from '~global/images/responsive-image'

export default {
  name: 'LineItem',

  components: {
    ProductPrices,
    QuantitySelector,
    ResponsiveImage,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    index: {
      type: Number,
      default: 0,
    },
    item: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    isDrawer: {
      type: Boolean,
      default: false,
    },
    isFreeGift: {
      type: Boolean,
      default: false,
    },
    freeGiftTarget: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      quantity: 1,
      state: 'loading',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cartStatus: (state) => state.cart.status,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute animation delay.
     * @returns {String}
     */
    animationDelay() {
      return this.$timing('quick', { index: this.index, unit: 's' })
    },

    itemTags() {
      if (typeof this.item?.tags === 'string') {
        return this.item?.tags.split(', ')
      }

      return this.item?.tags
    },

    /**
     * Product collection.
     * @returns {String|Boolean}
     */
    productCollection() {
      const collectionTag = 'Collection:'

      if (!this.item || !this.itemTags?.some(tag => tag.includes(collectionTag))) {
        return false
      }

      return this.itemTags?.find(tag => tag.includes(collectionTag))
        .replace(collectionTag, '').trim()
    },

    /**
     * Returns true if product is gift wrap.
     * @returns {Boolean}
     */
    isGiftWrapProduct() {
      return this.itemTags?.some(tag => tag.includes('Collection: Gift wrap'))
    },

    /**
     * Returns true if product type is made to order.
     * @returns {Boolean}
     */
    isMadeToOrderProduct() {
      return this.item?.properties.isMadeToOrder
    },

    /**
     * Returns true if its free gift.
     * @returns {Boolean}
     */
    isItemFreeGift() {
      return Object.keys(this.item?.properties || {}).some(property => property.includes('DiscountNinja.FreeGift'))
    },
  },

  watch: {

    /**
     * Watch cart status items to see if item is being updated.
     */
    'cartStatus.items'() {
      if (this.cartStatus.items.includes(this.item.key)) {
        this.state = 'updating'
        return
      }

      this.state = 'ready'
    },

    /**
     * Watch item quantity to update quantity if updated elsewhere.
     */
    'item.quantity'() {
      this.quantity = this.item.quantity
    },
  },

  mounted() {
    this.state = 'ready'
    this.quantity = this.item.quantity
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      updateItem: 'cart/updateItem',
      removeItem: 'cart/updateItem',
    }),

    /**
     * Handle quantity update event.
     * @param {String} quantity - New quantity.
     */
    async handleQuantityUpdate(quantity) {
      this.quantity = quantity

      try {
        this.state = 'updating'

        await this.updateItem([
          {
            id: this.item.key,
            quantity: this.quantity,
          },
        ])

        this.state = 'ready'

      } catch (error) {
        this.quantity = this.item.quantity
        this.state = 'error'
      }
    },

    /**
     * Handle remove click.
     */
    handleRemove() {
      if (this.state === 'updating') {
        return
      }

      this.removeItem(this.item.key)
    },
  },
}
</script>

<style lang="scss">
@import './line-item';
</style>
